import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type MarketCustomReportingExploreRoute from 'lumen/routes/market/custom-reporting/explore';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import type { ModelFrom } from 'lumen/utils/routes';

type LookerMessageData = {
  id: string;
};

export default class MarketCustomReportingExploreController extends Controller {
  declare model: ModelFrom<MarketCustomReportingExploreRoute>;

  // eslint-disable-next-line ember/no-unused-services
  @service declare ajax: AjaxService;
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service('looker-embed-session')
  declare lookerEmbedSession: Services['looker-embed-session'];
  @service declare router: Services['router'];

  @tracked embedError = false;
  @tracked embedUrl?: string;

  acquireEmbedUrlTask = task({ restartable: true }, async () => {
    this.embedUrl = undefined;
    this.embedError = false;

    try {
      await this.lookerEmbedSession.acquire({
        userType: 'custom_reporting',
        marketId: this.hotelAndMarketService.market.id ?? undefined,
      });
      this.embedUrl = this.lookerEmbedSession.buildLoginUrl(
        `/embed/explore/${this.model.modelName}/${this.model.exploreName}`,
      );
    } catch (error) {
      this.errorTracking.captureException(error);
      this.embedError = true;
    }
  });

  @action onMessage(type: string, data: LookerMessageData): void {
    // Since users can create a dashboard from the explore page,
    // we need to redirect them to the dashboard edit page
    if (type === 'dashboard:loaded') {
      this.router.transitionTo(
        'market.custom-reporting.dashboard.edit',
        data.id,
      );
    }
  }
}
